import { getOrganizationMembers, IOrganization, IUser } from "@/api";
import { UserScopeCount } from "@/components/admin/table/cells/UserScopeCount";
import { DateFilterInput } from "@/components/shared/table/DateFilterInput";
import { Table } from "@/components/shared/table/Table";
import { useFilterModel } from "@/hooks/useFilterModel";
import { formatDate } from "@/utils";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonIcon from "@mui/icons-material/Person";
import { Avatar, Box, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { AddNewUserButton } from "../buttons/AddNewUserButton";
import { RemoveUserFromOrganizationButton } from "../cells/RemoveUserFromOrganizationButton";

interface IProps {
    /**
     * the organization to display the users for
     */
    organization: IOrganization;
}

/**
 * a table that displays the users for an organization
 */
export const UsersTable = ({ organization }: IProps) => {
    const columns: GridColDef<IUser>[] = useMemo(
        () => [
            {
                field: "name",
                headerName: "Name",
                display: "flex",
                renderCell: (params) => {
                    return (
                        <Box display="flex" alignItems="center">
                            <Avatar alt="Avatar" variant="circular">
                                <PersonIcon />
                            </Avatar>
                            <Box ml={1}>
                                {params.row.first_name} {params.row.last_name}
                            </Box>
                        </Box>
                    );
                },
            },
            {
                field: "id",
                headerName: "ID",
                display: "flex",
            },
            {
                field: "email",
                headerName: "Email Address",
                display: "flex",
                renderCell: (params) => {
                    return (
                        <Box display="flex" alignItems="center">
                            <MailOutlineIcon />
                            <Box ml={1}>{params.row.email}</Box>
                        </Box>
                    );
                },
            },
            {
                field: "created_at",
                headerName: "Registration Date",
                display: "flex",
                type: "date",
                filterOperators: [
                    {
                        label: "Date is",
                        value: "dateIs",
                        getApplyFilterFn: (filterItem) => {
                            if (!filterItem.value) {
                                return null;
                            }
                            return ({ value }) => {
                                return value === filterItem.value;
                            };
                        },
                        InputComponent: DateFilterInput,
                    },
                ],
                valueFormatter: (value) => {
                    return formatDate(value, "PPp");
                },
            },

            {
                field: "number_of_scopes",
                headerName: "Number of Scopes",
                display: "flex",
                renderCell: (params) => {
                    return <UserScopeCount userId={params.row.id} />;
                },
            },

            {
                field: "role",
                headerName: "Permission",
                type: "singleSelect",
                display: "flex",
                valueOptions: ["Admin", "User"],
                valueGetter: (_, value) => {
                    return value.is_admin ? "Admin" : "User";
                },
            },
            {
                field: "actions",
                headerName: "Actions",
                type: "actions",
                getActions: ({ row }) => {
                    return [
                        <RemoveUserFromOrganizationButton
                            organizationId={organization.id}
                            userId={row.id}
                        />,
                    ];
                },
            },
        ],
        [organization.id]
    );

    const [rows, setRows] = useState<IUser[]>([]);

    const { filterModel, handleFilterModelChange } = useFilterModel();

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const autoSizeOptions = {
        columns: [
            "name",
            "email",
            "created_at",
            "number_of_scopes",
            "role",
            "actions",
        ],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    const { data, isPending } = useQuery({
        queryKey: [
            "admin-organization-members",
            organization.id,
            paginationModel.page,
            paginationModel.pageSize,
            filterModel,
        ],
        queryFn: () =>
            getOrganizationMembers(
                organization.id,
                paginationModel.page + 1,
                paginationModel.pageSize,
                filterModel
            ),
    });

    useEffect(() => {
        if (data) {
            setRows(data.items);
        }
    }, [data]);

    const rowCountRef = useRef(data?.total || 0);

    const rowCount = useMemo(() => {
        if (data?.total !== undefined) {
            rowCountRef.current = data?.total;
        }
        return rowCountRef.current;
    }, [data?.total]);

    return (
        <Box sx={{ mt: 2 }}>
            <Stack direction="row" justifyContent="space-between" mx={2}>
                <Typography variant="h6">Users</Typography>
                <AddNewUserButton organization={organization} />
            </Stack>
            <Box sx={{ mt: 2, mx: 1 }}>
                <Table
                    loading={isPending}
                    columns={columns}
                    rows={rows}
                    disableColumnResize
                    autosizeOptions={autoSizeOptions}
                    autoSize
                    pageSizeOptions={[10, 25, 50]}
                    paginationModel={paginationModel}
                    rowCount={rowCount}
                    onPaginationModelChange={setPaginationModel}
                    paginationMode="server"
                    filterMode="server"
                    sortingMode="server"
                    onFilterModelChange={handleFilterModelChange}
                    onSortModelChange={handleFilterModelChange}
                />
            </Box>
        </Box>
    );
};
